import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Trophy, Ship, Truck, AlertTriangle, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp, BarChart3, Target, Award, Flame, ArrowUpDown, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, Cell } from 'recharts';
import { toast } from 'sonner';

const API = window.location.origin;

const TABS = [
  { id: 'geral', label: 'Ranking Geral', icon: Trophy },
  { id: 'empurradores', label: 'Empurradores', icon: Truck },
  { id: 'balsas', label: 'Balsas', icon: Ship },
  { id: 'comparacao', label: 'Comparacao', icon: BarChart3 },
  { id: 'alertas', label: 'Alertas', icon: AlertTriangle },
  { id: 'evolucao', label: 'Evolucao', icon: TrendingUp },
];

const classifColor = (c) => {
  if (c === 'Excelente') return 'text-emerald-400';
  if (c === 'Regular') return 'text-amber-400';
  return 'text-red-400';
};

const classifBg = (c) => {
  if (c === 'Excelente') return 'bg-emerald-500/15 border-emerald-500/30';
  if (c === 'Regular') return 'bg-amber-500/15 border-amber-500/30';
  return 'bg-red-500/15 border-red-500/30';
};

const scoreBarColor = (score) => {
  if (score >= 90) return '#10b981';
  if (score >= 70) return '#3b82f6';
  if (score >= 50) return '#f59e0b';
  return '#ef4444';
};

const sevBg = (s) => {
  if (s === 'critica') return 'bg-red-500/15 border-red-500/40 text-red-300';
  if (s === 'alta') return 'bg-orange-500/15 border-orange-500/40 text-orange-300';
  return 'bg-amber-500/15 border-amber-500/40 text-amber-300';
};

const prioBg = (p) => {
  if (p === 'alta') return 'bg-red-500/20 text-red-300';
  if (p === 'media') return 'bg-orange-500/20 text-orange-300';
  if (p === 'baixa') return 'bg-blue-500/20 text-blue-300';
  return 'bg-slate-700 text-slate-300';
};

const DeltaIcon = ({ delta }) => {
  if (delta > 0) return <span className="flex items-center gap-0.5 text-emerald-400 text-xs font-mono">+{delta} <TrendingUp className="w-3 h-3" /></span>;
  if (delta < 0) return <span className="flex items-center gap-0.5 text-red-400 text-xs font-mono">{delta} <TrendingDown className="w-3 h-3" /></span>;
  return <span className="flex items-center gap-0.5 text-slate-500 text-xs font-mono">0 <Minus className="w-3 h-3" /></span>;
};

const MedalIcon = ({ pos }) => {
  if (pos === 1) return <span className="text-yellow-400 font-bold">1o</span>;
  if (pos === 2) return <span className="text-slate-300 font-bold">2o</span>;
  if (pos === 3) return <span className="text-amber-600 font-bold">3o</span>;
  return <span className="text-slate-500">{pos}o</span>;
};

const ScoreBar = ({ score, max = 110 }) => (
  <div className="flex items-center gap-2 flex-1">
    <div className="flex-1 bg-slate-800 rounded-full h-3 overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(score / max * 100, 100)}%`, backgroundColor: scoreBarColor(score) }} />
    </div>
    <span className="text-sm font-mono font-bold min-w-[36px] text-right" style={{ color: scoreBarColor(score) }}>{score}</span>
  </div>
);

const PERIOD_LABELS = { '2026-01': 'Jan/26', '2026-02': 'Fev/26', '2026-03': 'Mar/26', '2026-04': 'Abr/26' };

export default function RankingEmbarcacoesPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('geral');
  const [seeding, setSeeding] = useState(false);
  const [expandedRec, setExpandedRec] = useState(null);
  const [showAllGeral, setShowAllGeral] = useState(false);
  const [showAllEmp, setShowAllEmp] = useState(false);
  const [showAllBal, setShowAllBal] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ranking-embarcacoes/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      setData(json);
    } catch (e) {
      toast.error('Erro ao carregar ranking');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await fetch(`${API}/api/ranking-embarcacoes/seed`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      toast.success(json.message || 'Ranking populado');
      await fetchData();
    } catch { toast.error('Erro ao popular ranking'); }
    finally { setSeeding(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-cyan-400 font-mono">Carregando ranking...</div></div>;

  const isEmpty = !data || !data.ranking_geral || data.ranking_geral.length === 0;

  if (isEmpty) return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="ranking-title"><Trophy className="w-6 h-6 text-amber-400" />Ranking de Embarcacoes</h1>
      </div>
      <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-12 text-center">
        <Trophy className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400 mb-4">Nenhum dado de ranking encontrado. Popule os dados para comecar.</p>
        <button onClick={handleSeed} disabled={seeding} data-testid="seed-ranking-btn"
          className="px-6 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg font-medium transition disabled:opacity-50">
          {seeding ? 'Populando...' : 'Popular Dados de Ranking'}
        </button>
      </div>
    </div>
  );

  const { resumo, ranking_geral, empurradores, balsas, comparacao, evolucao, alertas, recomendacoes } = data;

  const geralBarData = (showAllGeral ? ranking_geral : ranking_geral.slice(0, 15)).map(x => ({
    nome: x.embarcacao_nome.length > 18 ? x.embarcacao_nome.slice(0, 16) + '..' : x.embarcacao_nome,
    pontuacao: x.pontuacao_arredondada, tipo: x.tipo, classificacao: x.classificacao,
  }));

  const empBarData = (showAllEmp ? empurradores : empurradores.slice(0, 15)).map(x => ({
    nome: x.embarcacao_nome.length > 18 ? x.embarcacao_nome.slice(0, 16) + '..' : x.embarcacao_nome,
    pontuacao: x.pontuacao_arredondada,
  }));

  const balBarData = (showAllBal ? balsas : balsas.slice(0, 15)).map(x => ({
    nome: x.embarcacao_nome.length > 18 ? x.embarcacao_nome.slice(0, 16) + '..' : x.embarcacao_nome,
    pontuacao: x.pontuacao_arredondada,
  }));

  const evoLineData = evolucao && evolucao.periodos ? evolucao.periodos.map(p => {
    const entry = { periodo: PERIOD_LABELS[p] || p };
    (evolucao.dados || []).slice(0, 10).forEach(d => { entry[d.embarcacao_nome] = d.periodos[p] || null; });
    return entry;
  }) : [];

  const evoColors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'];

  const compData = comparacao ? [
    { ind: 'Pontuacao Media', emp: comparacao.pontuacao_media?.empurradores || 0, bal: comparacao.pontuacao_media?.balsas || 0 },
    { ind: 'Disponibilidade', emp: comparacao.disponibilidade?.empurradores || 0, bal: comparacao.disponibilidade?.balsas || 0 },
    { ind: 'Preventiva %', emp: comparacao.cumprimento_preventiva?.empurradores || 0, bal: comparacao.cumprimento_preventiva?.balsas || 0 },
    { ind: 'Criticas Atrasadas', emp: comparacao.tarefas_criticas_atrasadas?.empurradores || 0, bal: comparacao.tarefas_criticas_atrasadas?.balsas || 0 },
    { ind: 'Tempo Resposta (h)', emp: comparacao.tempo_resposta_horas?.empurradores || 0, bal: comparacao.tempo_resposta_horas?.balsas || 0 },
    { ind: 'Corretivas', emp: comparacao.manutencoes_corretivas?.empurradores || 0, bal: comparacao.manutencoes_corretivas?.balsas || 0 },
    { ind: 'Horimetro %', emp: comparacao.conformidade_horimetro?.empurradores || 0, bal: comparacao.conformidade_horimetro?.balsas || 0 },
  ] : [];

  return (
    <div className="space-y-5" data-testid="ranking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="ranking-title">
            <Trophy className="w-6 h-6 text-amber-400" />Ranking de Embarcacoes
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Periodo: {data.periodo} | {resumo?.total_embarcacoes || 0} embarcacoes avaliadas</p>
        </div>
        <button onClick={fetchData} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm transition" data-testid="refresh-ranking">
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      {/* Summary Cards */}
      {resumo && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ranking-summary">
          <div className="bg-slate-900/80 border border-emerald-500/20 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Melhor do Mes</p>
            <p className="text-lg font-bold text-emerald-400 mt-1 truncate">{resumo.melhor_geral?.nome}</p>
            <p className="text-2xl font-mono font-bold text-white">{resumo.melhor_geral?.pontuacao}<span className="text-xs text-slate-500 ml-1">pts</span></p>
          </div>
          <div className="bg-slate-900/80 border border-red-500/20 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Pior do Mes</p>
            <p className="text-lg font-bold text-red-400 mt-1 truncate">{resumo.pior_geral?.nome}</p>
            <p className="text-2xl font-mono font-bold text-white">{resumo.pior_geral?.pontuacao}<span className="text-xs text-slate-500 ml-1">pts</span></p>
          </div>
          <div className="bg-slate-900/80 border border-cyan-500/20 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Media Empurradores</p>
            <p className="text-2xl font-mono font-bold text-cyan-400 mt-1">{resumo.media_empurradores}<span className="text-xs text-slate-500 ml-1">pts</span></p>
            <p className="text-xs text-slate-500">{empurradores?.length || 0} embarcacoes</p>
          </div>
          <div className="bg-slate-900/80 border border-blue-500/20 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Media Balsas</p>
            <p className="text-2xl font-mono font-bold text-blue-400 mt-1">{resumo.media_balsas}<span className="text-xs text-slate-500 ml-1">pts</span></p>
            <p className="text-xs text-slate-500">{balsas?.length || 0} embarcacoes</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-slate-800 pb-px" data-testid="ranking-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors ${tab === t.id ? 'bg-slate-800 text-white border-b-2 border-cyan-500' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'}`}>
              <Icon className="w-3.5 h-3.5" />{t.label}
              {t.id === 'alertas' && alertas?.length > 0 && <span className="ml-1 bg-red-500/30 text-red-300 text-xs px-1.5 rounded-full">{alertas.length}</span>}
            </button>
          );
        })}
      </div>

      {/* TAB: RANKING GERAL */}
      {tab === 'geral' && (
        <div className="space-y-4" data-testid="tab-geral-content">
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg overflow-hidden">
            <div className="p-3 border-b border-slate-800 flex items-center justify-between">
              <h2 className="text-base font-semibold text-white flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-400" />Ranking Geral - Todas as Embarcacoes</h2>
              {ranking_geral.length > 15 && <button onClick={() => setShowAllGeral(!showAllGeral)} className="text-xs text-cyan-400 hover:text-cyan-300">{showAllGeral ? 'Mostrar Top 15' : `Mostrar Todas (${ranking_geral.length})`}</button>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-800/60 text-slate-400 text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 text-left w-16">Pos.</th>
                  <th className="px-3 py-2 text-left">Embarcacao</th>
                  <th className="px-3 py-2 text-left">Tipo</th>
                  <th className="px-3 py-2 text-center w-48">Pontuacao</th>
                  <th className="px-3 py-2 text-center">Class.</th>
                  <th className="px-3 py-2 text-center">Delta</th>
                </tr></thead>
                <tbody>
                  {(showAllGeral ? ranking_geral : ranking_geral.slice(0, 15)).map((item, idx) => (
                    <tr key={item.embarcacao_id} className={`border-t border-slate-800/50 hover:bg-slate-800/30 transition ${idx < 3 ? 'bg-slate-800/20' : ''}`} data-testid={`ranking-row-${idx}`}>
                      <td className="px-3 py-2 font-mono"><MedalIcon pos={item.posicao} /></td>
                      <td className="px-3 py-2 font-medium text-white">{item.embarcacao_nome}</td>
                      <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded ${item.tipo === 'empurrador' ? 'bg-cyan-500/15 text-cyan-400' : 'bg-blue-500/15 text-blue-400'}`}>{item.tipo === 'empurrador' ? 'Empurrador' : 'Balsa'}</span></td>
                      <td className="px-3 py-2"><ScoreBar score={item.pontuacao_arredondada} /></td>
                      <td className="px-3 py-2 text-center"><span className={`text-xs font-medium ${classifColor(item.classificacao)}`}>{item.classificacao}</span></td>
                      <td className="px-3 py-2 text-center"><DeltaIcon delta={item.delta} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Bar Chart */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-white mb-3">Grafico de Barras - Ranking Geral</h3>
            <ResponsiveContainer width="100%" height={Math.max(300, geralBarData.length * 28)}>
              <BarChart data={geralBarData} layout="vertical" margin={{ left: 10, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis type="number" domain={[0, 110]} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis type="category" dataKey="nome" width={130} tick={{ fill: '#cbd5e1', fontSize: 11 }} />
                <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, color: '#f8fafc' }} />
                <Bar dataKey="pontuacao" radius={[0, 4, 4, 0]}>
                  {geralBarData.map((entry, i) => <Cell key={i} fill={scoreBarColor(entry.pontuacao)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* TAB: EMPURRADORES */}
      {tab === 'empurradores' && (
        <div className="space-y-4" data-testid="tab-empurradores-content">
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg overflow-hidden">
            <div className="p-3 border-b border-slate-800 flex items-center justify-between">
              <h2 className="text-base font-semibold text-white flex items-center gap-2"><Truck className="w-4 h-4 text-cyan-400" />Ranking entre Empurradores ({empurradores.length})</h2>
              {empurradores.length > 15 && <button onClick={() => setShowAllEmp(!showAllEmp)} className="text-xs text-cyan-400 hover:text-cyan-300">{showAllEmp ? 'Top 15' : 'Mostrar Todos'}</button>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-800/60 text-slate-400 text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 text-left w-12">Pos.</th>
                  <th className="px-3 py-2 text-left">Empurrador</th>
                  <th className="px-3 py-2 text-center w-40">Pontuacao</th>
                  <th className="px-3 py-2 text-center">Class.</th>
                  <th className="px-3 py-2 text-center">Disp.</th>
                  <th className="px-3 py-2 text-center">Prev. %</th>
                  <th className="px-3 py-2 text-center">Crit. Atr.</th>
                  <th className="px-3 py-2 text-center">Resp. (h)</th>
                  <th className="px-3 py-2 text-center">Corretivas</th>
                  <th className="px-3 py-2 text-center">MTBF (h)</th>
                  <th className="px-3 py-2 text-center">MTTR (h)</th>
                </tr></thead>
                <tbody>
                  {(showAllEmp ? empurradores : empurradores.slice(0, 15)).map((item, idx) => (
                    <tr key={item.embarcacao_id} className="border-t border-slate-800/50 hover:bg-slate-800/30 transition" data-testid={`emp-row-${idx}`}>
                      <td className="px-3 py-2 font-mono"><MedalIcon pos={item.posicao_categoria} /></td>
                      <td className="px-3 py-2 font-medium text-white">{item.embarcacao_nome}</td>
                      <td className="px-3 py-2"><ScoreBar score={item.pontuacao_arredondada} /></td>
                      <td className="px-3 py-2 text-center"><span className={`text-xs font-medium ${classifColor(item.classificacao)}`}>{item.classificacao}</span></td>
                      <td className="px-3 py-2 text-center text-slate-300 font-mono text-xs">{item.indicadores.disponibilidade}%</td>
                      <td className="px-3 py-2 text-center text-slate-300 font-mono text-xs">{item.indicadores.cumprimento_preventiva}%</td>
                      <td className="px-3 py-2 text-center font-mono text-xs"><span className={item.indicadores.tarefas_criticas_atrasadas > 2 ? 'text-red-400' : 'text-slate-300'}>{item.indicadores.tarefas_criticas_atrasadas}</span></td>
                      <td className="px-3 py-2 text-center text-slate-300 font-mono text-xs">{item.indicadores.tempo_resposta_horas}h</td>
                      <td className="px-3 py-2 text-center font-mono text-xs"><span className={item.indicadores.manutencoes_corretivas > 3 ? 'text-red-400' : 'text-slate-300'}>{item.indicadores.manutencoes_corretivas}</span></td>
                      <td className="px-3 py-2 text-center font-mono text-xs"><span className={(item.detalhes?.mtbf_motor || 0) < 400 ? 'text-red-400' : 'text-slate-300'}>{item.detalhes?.mtbf_motor || '-'}</span></td>
                      <td className="px-3 py-2 text-center text-slate-300 font-mono text-xs">{item.detalhes?.mttr || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Empurradores Bar Chart */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-white mb-3">Grafico - Ranking Empurradores</h3>
            <ResponsiveContainer width="100%" height={Math.max(250, empBarData.length * 28)}>
              <BarChart data={empBarData} layout="vertical" margin={{ left: 10, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis type="number" domain={[0, 110]} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis type="category" dataKey="nome" width={130} tick={{ fill: '#cbd5e1', fontSize: 11 }} />
                <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, color: '#f8fafc' }} />
                <Bar dataKey="pontuacao" radius={[0, 4, 4, 0]}>
                  {empBarData.map((entry, i) => <Cell key={i} fill={scoreBarColor(entry.pontuacao)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* Indicadores Detalhados */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg overflow-hidden">
            <div className="p-3 border-b border-slate-800"><h3 className="text-sm font-semibold text-white">Indicadores Detalhados por Empurrador</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-800/60 text-slate-400 uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">Indicador</th>
                  {(showAllEmp ? empurradores : empurradores.slice(0, 8)).map(e => <th key={e.embarcacao_id} className="px-2 py-2 text-center">{e.embarcacao_nome.length > 14 ? e.embarcacao_nome.slice(0, 12) + '..' : e.embarcacao_nome}</th>)}
                </tr></thead>
                <tbody>
                  <tr className="border-t border-slate-800/50"><td className="px-3 py-1.5 text-slate-400">Horimetro Motor</td>
                    {(showAllEmp ? empurradores : empurradores.slice(0, 8)).map(e => <td key={e.embarcacao_id} className="px-2 py-1.5 text-center text-slate-300 font-mono">{e.detalhes?.horimetro_motor?.atual || '-'}/{e.detalhes?.horimetro_motor?.limite || '-'} ({e.detalhes?.horimetro_motor?.pct || 0}%)</td>)}</tr>
                  <tr className="border-t border-slate-800/50"><td className="px-3 py-1.5 text-slate-400">Horimetro Gerador 1</td>
                    {(showAllEmp ? empurradores : empurradores.slice(0, 8)).map(e => <td key={e.embarcacao_id} className="px-2 py-1.5 text-center text-slate-300 font-mono">{e.detalhes?.horimetro_gerador1?.atual || '-'}/{e.detalhes?.horimetro_gerador1?.limite || '-'} ({e.detalhes?.horimetro_gerador1?.pct || 0}%)</td>)}</tr>
                  <tr className="border-t border-slate-800/50"><td className="px-3 py-1.5 text-slate-400">MTBF Motor (h)</td>
                    {(showAllEmp ? empurradores : empurradores.slice(0, 8)).map(e => <td key={e.embarcacao_id} className={`px-2 py-1.5 text-center font-mono ${(e.detalhes?.mtbf_motor || 0) < 400 ? 'text-red-400' : 'text-slate-300'}`}>{e.detalhes?.mtbf_motor || '-'}</td>)}</tr>
                  <tr className="border-t border-slate-800/50"><td className="px-3 py-1.5 text-slate-400">MTTR (h)</td>
                    {(showAllEmp ? empurradores : empurradores.slice(0, 8)).map(e => <td key={e.embarcacao_id} className="px-2 py-1.5 text-center text-slate-300 font-mono">{e.detalhes?.mttr || '-'}</td>)}</tr>
                  <tr className="border-t border-slate-800/50"><td className="px-3 py-1.5 text-slate-400">Disponibilidade Motor</td>
                    {(showAllEmp ? empurradores : empurradores.slice(0, 8)).map(e => <td key={e.embarcacao_id} className="px-2 py-1.5 text-center text-slate-300 font-mono">{e.detalhes?.disponibilidade_motor || e.bonus?.disponibilidade_motor || '-'}%</td>)}</tr>
                  <tr className="border-t border-slate-800/50"><td className="px-3 py-1.5 text-slate-400">Conformidade Horimetro</td>
                    {(showAllEmp ? empurradores : empurradores.slice(0, 8)).map(e => <td key={e.embarcacao_id} className="px-2 py-1.5 text-center text-slate-300 font-mono">{e.indicadores?.conformidade_horimetro || '-'}%</td>)}</tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB: BALSAS */}
      {tab === 'balsas' && (
        <div className="space-y-4" data-testid="tab-balsas-content">
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg overflow-hidden">
            <div className="p-3 border-b border-slate-800 flex items-center justify-between">
              <h2 className="text-base font-semibold text-white flex items-center gap-2"><Ship className="w-4 h-4 text-blue-400" />Ranking entre Balsas ({balsas.length})</h2>
              {balsas.length > 15 && <button onClick={() => setShowAllBal(!showAllBal)} className="text-xs text-cyan-400 hover:text-cyan-300">{showAllBal ? 'Top 15' : 'Mostrar Todas'}</button>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-800/60 text-slate-400 text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 text-left w-12">Pos.</th>
                  <th className="px-3 py-2 text-left">Balsa</th>
                  <th className="px-3 py-2 text-center w-40">Pontuacao</th>
                  <th className="px-3 py-2 text-center">Class.</th>
                  <th className="px-3 py-2 text-center">Disp.</th>
                  <th className="px-3 py-2 text-center">Bombas OK</th>
                  <th className="px-3 py-2 text-center">Insp. Estrutural</th>
                  <th className="px-3 py-2 text-center">Lastro</th>
                  <th className="px-3 py-2 text-center">Corretivas</th>
                </tr></thead>
                <tbody>
                  {(showAllBal ? balsas : balsas.slice(0, 15)).map((item, idx) => (
                    <tr key={item.embarcacao_id} className="border-t border-slate-800/50 hover:bg-slate-800/30 transition" data-testid={`bal-row-${idx}`}>
                      <td className="px-3 py-2 font-mono"><MedalIcon pos={item.posicao_categoria} /></td>
                      <td className="px-3 py-2 font-medium text-white">{item.embarcacao_nome}</td>
                      <td className="px-3 py-2"><ScoreBar score={item.pontuacao_arredondada} /></td>
                      <td className="px-3 py-2 text-center"><span className={`text-xs font-medium ${classifColor(item.classificacao)}`}>{item.classificacao}</span></td>
                      <td className="px-3 py-2 text-center text-slate-300 font-mono text-xs">{item.indicadores.disponibilidade}%</td>
                      <td className="px-3 py-2 text-center font-mono text-xs"><span className={(item.detalhes?.bombas_operacionais?.pct || 100) < 90 ? 'text-amber-400' : 'text-slate-300'}>{item.detalhes?.bombas_operacionais?.ok || '-'}/{item.detalhes?.bombas_operacionais?.total || '-'} ({item.detalhes?.bombas_operacionais?.pct || 0}%)</span></td>
                      <td className="px-3 py-2 text-center text-slate-300 font-mono text-xs">{item.detalhes?.inspecoes_estruturais || '-'}%</td>
                      <td className="px-3 py-2 text-center text-slate-300 font-mono text-xs">{item.detalhes?.conformidade_lastro || item.bonus?.conformidade_lastro || '-'}%</td>
                      <td className="px-3 py-2 text-center font-mono text-xs"><span className={item.indicadores.manutencoes_corretivas > 3 ? 'text-red-400' : 'text-slate-300'}>{item.indicadores.manutencoes_corretivas}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Balsas Bar Chart */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-white mb-3">Grafico - Ranking Balsas</h3>
            <ResponsiveContainer width="100%" height={Math.max(250, balBarData.length * 28)}>
              <BarChart data={balBarData} layout="vertical" margin={{ left: 10, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis type="number" domain={[0, 110]} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis type="category" dataKey="nome" width={130} tick={{ fill: '#cbd5e1', fontSize: 11 }} />
                <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, color: '#f8fafc' }} />
                <Bar dataKey="pontuacao" radius={[0, 4, 4, 0]}>
                  {balBarData.map((entry, i) => <Cell key={i} fill={scoreBarColor(entry.pontuacao)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* Indicadores Detalhados Balsas */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg overflow-hidden">
            <div className="p-3 border-b border-slate-800"><h3 className="text-sm font-semibold text-white">Indicadores Detalhados por Balsa</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-800/60 text-slate-400 uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">Indicador</th>
                  {(showAllBal ? balsas : balsas.slice(0, 8)).map(e => <th key={e.embarcacao_id} className="px-2 py-2 text-center">{e.embarcacao_nome.length > 14 ? e.embarcacao_nome.slice(0, 12) + '..' : e.embarcacao_nome}</th>)}
                </tr></thead>
                <tbody>
                  <tr className="border-t border-slate-800/50"><td className="px-3 py-1.5 text-slate-400">Gerador Disp.</td>
                    {(showAllBal ? balsas : balsas.slice(0, 8)).map(e => <td key={e.embarcacao_id} className="px-2 py-1.5 text-center text-slate-300 font-mono">{e.detalhes?.gerador_disponibilidade || '-'}%</td>)}</tr>
                  <tr className="border-t border-slate-800/50"><td className="px-3 py-1.5 text-slate-400">Horimetro Gerador</td>
                    {(showAllBal ? balsas : balsas.slice(0, 8)).map(e => <td key={e.embarcacao_id} className="px-2 py-1.5 text-center text-slate-300 font-mono">{e.detalhes?.horimetro_gerador?.atual || '-'}/{e.detalhes?.horimetro_gerador?.limite || '-'}</td>)}</tr>
                  <tr className="border-t border-slate-800/50"><td className="px-3 py-1.5 text-slate-400">Bombas Oper.</td>
                    {(showAllBal ? balsas : balsas.slice(0, 8)).map(e => <td key={e.embarcacao_id} className="px-2 py-1.5 text-center text-slate-300 font-mono">{e.detalhes?.bombas_operacionais?.ok || '-'}/{e.detalhes?.bombas_operacionais?.total || '-'}</td>)}</tr>
                  <tr className="border-t border-slate-800/50"><td className="px-3 py-1.5 text-slate-400">Inspecoes Estr.</td>
                    {(showAllBal ? balsas : balsas.slice(0, 8)).map(e => <td key={e.embarcacao_id} className="px-2 py-1.5 text-center text-slate-300 font-mono">{e.detalhes?.inspecoes_estruturais || '-'}%</td>)}</tr>
                  <tr className="border-t border-slate-800/50"><td className="px-3 py-1.5 text-slate-400">Testes de Carga</td>
                    {(showAllBal ? balsas : balsas.slice(0, 8)).map(e => <td key={e.embarcacao_id} className="px-2 py-1.5 text-center text-slate-300 font-mono">{e.detalhes?.testes_carga || '-'}%</td>)}</tr>
                  <tr className="border-t border-slate-800/50"><td className="px-3 py-1.5 text-slate-400">Conf. Lastro</td>
                    {(showAllBal ? balsas : balsas.slice(0, 8)).map(e => <td key={e.embarcacao_id} className="px-2 py-1.5 text-center text-slate-300 font-mono">{e.detalhes?.conformidade_lastro || '-'}%</td>)}</tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB: COMPARACAO */}
      {tab === 'comparacao' && (
        <div className="space-y-4" data-testid="tab-comparacao-content">
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg overflow-hidden">
            <div className="p-3 border-b border-slate-800"><h2 className="text-base font-semibold text-white flex items-center gap-2"><ArrowUpDown className="w-4 h-4 text-purple-400" />Comparacao entre Categorias (Empurradores vs. Balsas)</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-800/60 text-slate-400 text-xs uppercase tracking-wider">
                  <th className="px-4 py-2 text-left">Indicador</th>
                  <th className="px-4 py-2 text-center">Media Empurradores</th>
                  <th className="px-4 py-2 text-center">Media Balsas</th>
                  <th className="px-4 py-2 text-center">Melhor</th>
                  <th className="px-4 py-2 text-center">Diferenca</th>
                </tr></thead>
                <tbody>
                  {compData.map((row, i) => {
                    const diff = Math.abs(row.emp - row.bal);
                    const isInverse = row.ind.includes('Criticas') || row.ind.includes('Corretivas') || row.ind.includes('Tempo');
                    const better = row.emp === row.bal ? 'Empate' : (isInverse ? (row.emp < row.bal ? 'Empurradores' : 'Balsas') : (row.emp > row.bal ? 'Empurradores' : 'Balsas'));
                    return (
                      <tr key={i} className="border-t border-slate-800/50 hover:bg-slate-800/30">
                        <td className="px-4 py-2.5 text-white font-medium">{row.ind}</td>
                        <td className="px-4 py-2.5 text-center font-mono text-cyan-300">{row.emp}</td>
                        <td className="px-4 py-2.5 text-center font-mono text-blue-300">{row.bal}</td>
                        <td className="px-4 py-2.5 text-center"><span className={better === 'Empate' ? 'text-slate-400' : better === 'Balsas' ? 'text-blue-400' : 'text-cyan-400'}>{better}</span></td>
                        <td className="px-4 py-2.5 text-center font-mono text-slate-400">{diff === 0 ? '-' : diff.toFixed(1)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {/* Comparison Bar */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-white mb-3">Pontuacao Media por Categoria</h3>
            <ResponsiveContainer width="100%" height={120}>
              <BarChart data={[{ nome: 'Empurradores', pontuacao: comparacao?.pontuacao_media?.empurradores || 0 }, { nome: 'Balsas', pontuacao: comparacao?.pontuacao_media?.balsas || 0 }]} layout="vertical" margin={{ left: 10, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis type="number" domain={[0, 110]} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis type="category" dataKey="nome" width={100} tick={{ fill: '#cbd5e1', fontSize: 12 }} />
                <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, color: '#f8fafc' }} />
                <Bar dataKey="pontuacao" radius={[0, 6, 6, 0]}>
                  <Cell fill="#06b6d4" />
                  <Cell fill="#3b82f6" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* TAB: ALERTAS */}
      {tab === 'alertas' && (
        <div className="space-y-4" data-testid="tab-alertas-content">
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg">
            <div className="p-3 border-b border-slate-800"><h2 className="text-base font-semibold text-white flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" />Alertas Automaticos ({alertas?.length || 0})</h2></div>
            {alertas && alertas.length > 0 ? (
              <div className="p-3 space-y-2">
                {alertas.map((a, i) => (
                  <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${sevBg(a.severidade)}`} data-testid={`alerta-${i}`}>
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{a.embarcacao}</p>
                      <p className="text-xs mt-0.5 opacity-80">{a.mensagem}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-xs opacity-60">
                        <span>Tipo: {a.tipo.replace(/_/g, ' ')}</span>
                        <span>Dest.: {a.destinatario}</span>
                        <span className="capitalize">Cat.: {a.categoria}</span>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded uppercase font-bold ${a.severidade === 'critica' ? 'bg-red-500/30 text-red-300' : a.severidade === 'alta' ? 'bg-orange-500/30 text-orange-300' : 'bg-amber-500/30 text-amber-300'}`}>{a.severidade}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">Nenhum alerta ativo no periodo.</div>
            )}
          </div>
          {/* Recommendations */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg">
            <div className="p-3 border-b border-slate-800"><h2 className="text-base font-semibold text-white flex items-center gap-2"><Target className="w-4 h-4 text-emerald-400" />Recomendacoes por Embarcacao</h2></div>
            <div className="p-3 space-y-2">
              {(recomendacoes || []).filter(r => r.recomendacoes.some(x => x.prioridade !== 'info')).slice(0, 10).map((rec, i) => (
                <div key={i} className="border border-slate-800 rounded-lg overflow-hidden">
                  <button onClick={() => setExpandedRec(expandedRec === i ? null : i)}
                    className="w-full flex items-center justify-between p-3 hover:bg-slate-800/50 transition text-left" data-testid={`rec-${i}`}>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-bold ${classifColor(rec.classificacao)}`}>{rec.pontuacao}</span>
                      <span className="text-white text-sm font-medium">{rec.embarcacao_nome}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${rec.tipo === 'empurrador' ? 'bg-cyan-500/15 text-cyan-400' : 'bg-blue-500/15 text-blue-400'}`}>{rec.tipo}</span>
                    </div>
                    {expandedRec === i ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </button>
                  {expandedRec === i && (
                    <div className="px-3 pb-3 space-y-1.5">
                      {rec.recomendacoes.map((r, j) => (
                        <div key={j} className="flex items-start gap-3 text-xs bg-slate-800/50 rounded p-2">
                          <span className={`px-1.5 py-0.5 rounded font-bold uppercase ${prioBg(r.prioridade)}`}>{r.prioridade}</span>
                          <div className="flex-1"><p className="text-white">{r.problema}</p><p className="text-slate-400 mt-0.5">{r.recomendacao}</p></div>
                          <span className="text-slate-500 whitespace-nowrap">{r.prazo}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TAB: EVOLUCAO */}
      {tab === 'evolucao' && (
        <div className="space-y-4" data-testid="tab-evolucao-content">
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-4">
            <h2 className="text-base font-semibold text-white flex items-center gap-2 mb-4"><TrendingUp className="w-4 h-4 text-emerald-400" />Evolucao da Pontuacao (Ultimos 4 Meses) - Top 10</h2>
            {evoLineData.length > 0 ? (
              <ResponsiveContainer width="100%" height={380}>
                <LineChart data={evoLineData} margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="periodo" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <YAxis domain={[40, 110]} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, color: '#f8fafc', fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {(evolucao?.dados || []).slice(0, 10).map((d, i) => (
                    <Line key={d.embarcacao_nome} type="monotone" dataKey={d.embarcacao_nome} stroke={evoColors[i % evoColors.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-500 text-center py-8">Dados de evolucao nao disponiveis.</p>
            )}
          </div>
          {/* Evolution Table */}
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg overflow-hidden">
            <div className="p-3 border-b border-slate-800"><h3 className="text-sm font-semibold text-white">Tabela de Evolucao</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-800/60 text-slate-400 text-xs uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">Embarcacao</th>
                  <th className="px-3 py-2 text-center">Tipo</th>
                  {(evolucao?.periodos || []).map(p => <th key={p} className="px-3 py-2 text-center">{PERIOD_LABELS[p] || p}</th>)}
                  <th className="px-3 py-2 text-center">Tendencia</th>
                </tr></thead>
                <tbody>
                  {(evolucao?.dados || []).slice(0, 20).map((d, idx) => {
                    const vals = (evolucao?.periodos || []).map(p => d.periodos[p] || 0);
                    const trend = vals.length >= 2 ? vals[vals.length - 1] - vals[0] : 0;
                    return (
                      <tr key={idx} className="border-t border-slate-800/50 hover:bg-slate-800/30">
                        <td className="px-3 py-2 font-medium text-white">{d.embarcacao_nome}</td>
                        <td className="px-3 py-2 text-center"><span className={`text-xs px-2 py-0.5 rounded ${d.tipo === 'empurrador' ? 'bg-cyan-500/15 text-cyan-400' : 'bg-blue-500/15 text-blue-400'}`}>{d.tipo === 'empurrador' ? 'Emp' : 'Bal'}</span></td>
                        {(evolucao?.periodos || []).map(p => {
                          const v = d.periodos[p];
                          return <td key={p} className="px-3 py-2 text-center font-mono text-sm" style={{ color: v ? scoreBarColor(v) : '#64748b' }}>{v || '-'}</td>;
                        })}
                        <td className="px-3 py-2 text-center"><DeltaIcon delta={Math.round(trend)} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
